import * as React from 'react'

import GenericError from 'main/components/common/GenericError'
import Loader from 'main/components/common/Loader'
import Callout, {
  Target as CalloutTarget,
  TargetType as CalloutTargetType,
  UpcomingEvent,
  UpcomingEventType,
} from 'main/components/school/Callout'
import { useWhitelabel } from 'main/framework'
import { SchoolListingType } from 'main/gql/globalTypes'
import useAppFeatureFlags from 'main/hooks/useAppFeatureFlags'
import { Button, Hidden, palette, Slider, Strong, Text, Visible } from 'main/ui'
import { determineImage } from 'main/util/school'
import { IFilters, IRankingFactorWeights } from 'main/util/search'
import { UnreachableCaseError } from 'main/util/ts'

import { LOCIZE_NAMESPACE } from 'main/common'
import { i18nKeyPrefix } from 'main/pages/Search/i18n'
import { AssetName } from 'main/util/asset'
import { useTranslation } from 'react-i18next'
import { SearchPage_result, SearchPage_result_listings } from '../../gql'
import { NetworkContactDetails } from '../NetworkContactDetails'
import SaveSearchForm from '../SaveSearchForm'
import SubsidyEligibility from '../SubsidyEligibility'
import {
  CalloutSectionContainer,
  Container,
  DistanceSliderContainer,
  ListingsHeaderContainer,
  ListingsRow,
  LoadMoreContainer,
} from './Content.styles'
import NoResults from './NoResults'
import RankingWeights from './RankingWeights'

const PRE = `${i18nKeyPrefix}-LISTING-SECTION`

interface IProps {
  error: boolean
  fetchingMore: boolean
  loading: boolean
  result: SearchPage_result | null
  filters: IFilters
  rankingFactorWeights: IRankingFactorWeights | null
  searchRadius: number
  usingRadius: boolean
  onChangeRankingWeights: (rankingWeights: IRankingFactorWeights) => void
  onChangeSearchRadius: (radius: number) => void
  onRequestNextPage: () => void
}

export default function Content(props: IProps) {
  const {
    error,
    fetchingMore,
    loading,
    result,
    filters,
    rankingFactorWeights,
    searchRadius,
    usingRadius,
    onChangeRankingWeights,
    onChangeSearchRadius,
    onRequestNextPage,
  } = props
  const { t } = useTranslation(LOCIZE_NAMESPACE)
  // Conversions from default radius:
  // 10mi -> value of 60 on the slider
  // 30mi -> value of 100 on the slider
  const initialDistance = searchRadius === 10 ? 60 : 100
  const [distance, setDistance] = React.useState(initialDistance)
  const { appFeatureFlagEnabled } = useAppFeatureFlags()

  if (error) return <GenericError />
  if (loading || !result) return <Loader />

  const { location } = result
  const total = result.allCoordinates ? result.allCoordinates.length : 0
  const current = result.listings.length

  const whitelabel = useWhitelabel()

  return (
    <Container>
      <ListingsHeaderContainer>
        {appFeatureFlagEnabled('legacy_search_redirect_banner') && (
          <div>An improved search experience is coming soon</div>
        )}
        <ListingsRow>
          <DistanceSliderContainer>
            <Strong>{t(`${PRE}-DISTANCE`)}</Strong>
            <Slider
              minValue={0}
              maxValue={100}
              value={distance}
              step={10}
              onChange={setDistance}
              onAfterChange={value => onChangeSearchRadius(convertToMiles(value))}
              withoutLabel
            />
            <Text muted>0mi - {distance === 0 ? 0.5 : convertToMiles(distance)}mi</Text>
          </DistanceSliderContainer>
          <NetworkContactDetails />
        </ListingsRow>
        {current ? (
          <Strong css={{ color: palette.slate }}>
            {usingRadius
              ? t(`${PRE}-RESULTS-RADIUS`, { total, current, searchRadius })
              : t(`${PRE}-RESULTS`, { total, current })}
          </Strong>
        ) : null}
        <RankingWeights
          rankingWeights={rankingFactorWeights}
          onChangeRankingWeights={onChangeRankingWeights}
        />
      </ListingsHeaderContainer>

      {whitelabel.hideByDefaultExceptIfTrue(AssetName.SUBSIDY_ELIGIBILITY_FORM) && (
        <SubsidyEligibility />
      )}

      {!current ? <NoResults filters={filters} /> : null}

      <Visible md lg xl>
        <CalloutSectionContainer>
          {result.listings.slice(0, 6).map((listing, i) => renderCallout(listing, filters, i, 1))}
        </CalloutSectionContainer>
        {renderSaveSearchForm()}
        <CalloutSectionContainer>
          {result.listings.slice(6).map((listing, i) => renderCallout(listing, filters, i, 7))}
        </CalloutSectionContainer>
      </Visible>

      <Hidden md lg xl>
        <CalloutSectionContainer>
          {result.listings.slice(0, 3).map((listing, i) => renderCallout(listing, filters, i, 1))}
        </CalloutSectionContainer>
        {renderSaveSearchForm()}
        <CalloutSectionContainer>
          {result.listings.slice(3).map((listing, i) => renderCallout(listing, filters, i, 4))}
        </CalloutSectionContainer>
      </Hidden>

      {total > current && (
        <LoadMoreContainer>
          <Button disabled={fetchingMore} kind="outline" onClick={onRequestNextPage}>
            {fetchingMore ? t(`${PRE}-FETCHING-MORE`) : t(`${PRE}-FETCH`)}
          </Button>
        </LoadMoreContainer>
      )}
    </Container>
  )

  function renderCallout(
    listing: SearchPage_result_listings,
    seacrhFilters: IFilters,
    i: number,
    pendoNumber: number,
  ) {
    const { externalSchoolId, imageUrl, schoolId } = listing

    const coverImage = determineImage(
      listing.type === SchoolListingType.INTERNAL,
      schoolId || externalSchoolId,
      whitelabel,
      imageUrl,
    )

    return (
      <div key={i} data-pendo={pendoTag(listing, i + pendoNumber)}>
        <Callout
          key={i}
          target={targetForListing(listing)}
          upcomingEvent={upcomingEventForListing(listing)}
          coverImageUrl={coverImage}
          showWeeklyPricesOnly={seacrhFilters.acceptsWeeklyBilling}
        />
      </div>
    )
  }

  function renderSaveSearchForm() {
    return (
      <React.Fragment>
        {whitelabel.showByDefaultExceptIfFalse(AssetName.SAVE_SEARCH_FORM) && (
          <SaveSearchForm location={location} loggedOutTitle={t(`${PRE}-LOGGED-OUT-TITLE`)} />
        )}
      </React.Fragment>
    )
  }
}

function convertToMiles(sliderValue: number | number[] | undefined): number {
  // (neftaly) Return default miles if null | undefined or if is a range
  if (sliderValue == null || Array.isArray(sliderValue)) {
    return 5
  }

  const searchRadiusMarks = new Map(
    Object.entries({
      0: 0,
      10: 1,
      20: 2,
      30: 3,
      40: 4,
      50: 5,
      60: 10,
      70: 15,
      80: 20,
      90: 25,
      100: 30,
    }),
  )

  return searchRadiusMarks.get(sliderValue.toString()) ?? 10
}

function pendoTag(listing: SearchPage_result_listings, index: number) {
  switch (listing.type) {
    case SchoolListingType.INTERNAL:
      return `searchResultsSlot-${index}-wonderschool`
    case SchoolListingType.EXTERNAL:
      return `seachResultsSlot-${index}-externalSchool`
    default:
      throw new UnreachableCaseError(listing.type)
  }
}

function targetForListing(listing: SearchPage_result_listings): CalloutTarget {
  return {
    _type: CalloutTargetType.SchoolListing,
    listing,
  }
}

function upcomingEventForListing(listing: SearchPage_result_listings): UpcomingEvent | null {
  const { upcomingGatherings } = listing
  const gathering = upcomingGatherings[0]
  if (!gathering) return null

  return {
    __type: UpcomingEventType.Gathering,
    gathering,
  }
}
